/**
 * Nested editor for an item's customization groups. Each group has a name,
 * type (single/multi), required flag, and a list of options. Each option has
 * a label, a price delta (entered as dollars but stored as cents), and an
 * isDefault flag — at most one default per `single` group.
 */
import type {
  CustomizationGroup,
  CustomizationGroupType,
  CustomizationOption,
} from '@cracked-mug/shared';
import { Plus, Trash2 } from 'lucide-react';
import { fieldClass } from '../../components/ui/fieldStyles';
import { cn } from '../../lib/cn';

interface Props {
  groups: CustomizationGroup[];
  onChange: (groups: CustomizationGroup[]) => void;
}

function genId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function emptyGroup(): CustomizationGroup {
  return {
    groupId: genId('grp'),
    name: 'New group',
    type: 'single',
    required: false,
    options: [emptyOption(true)],
  };
}

function emptyOption(isDefault = false): CustomizationOption {
  return {
    optionId: genId('opt'),
    label: 'New option',
    priceDeltaCents: 0,
    isDefault,
  };
}

export function CustomizationGroupsEditor({ groups, onChange }: Props) {
  const updateGroup = (idx: number, patch: Partial<CustomizationGroup>): void => {
    onChange(groups.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };

  const removeGroup = (idx: number): void => {
    onChange(groups.filter((_, i) => i !== idx));
  };

  const addGroup = (): void => {
    onChange([...groups, emptyGroup()]);
  };

  const updateOption = (
    groupIdx: number,
    optIdx: number,
    patch: Partial<CustomizationOption>,
  ): void => {
    onChange(
      groups.map((g, i) => {
        if (i !== groupIdx) return g;
        return {
          ...g,
          options: g.options.map((o, j) => (j === optIdx ? { ...o, ...patch } : o)),
        };
      }),
    );
  };

  const setDefault = (groupIdx: number, optIdx: number, isDefault: boolean): void => {
    onChange(
      groups.map((g, i) => {
        if (i !== groupIdx) return g;
        if (g.type === 'single' && isDefault) {
          // Only one default allowed in single groups.
          return {
            ...g,
            options: g.options.map((o, j) => ({ ...o, isDefault: j === optIdx })),
          };
        }
        return {
          ...g,
          options: g.options.map((o, j) => (j === optIdx ? { ...o, isDefault } : o)),
        };
      }),
    );
  };

  const removeOption = (groupIdx: number, optIdx: number): void => {
    onChange(
      groups.map((g, i) => {
        if (i !== groupIdx) return g;
        return { ...g, options: g.options.filter((_, j) => j !== optIdx) };
      }),
    );
  };

  const addOption = (groupIdx: number): void => {
    onChange(
      groups.map((g, i) => {
        if (i !== groupIdx) return g;
        return { ...g, options: [...g.options, emptyOption(false)] };
      }),
    );
  };

  return (
    <div className="space-y-4">
      {groups.length === 0 && (
        <p className="text-sm text-wca-gray-mid italic">
          No customization groups. Items without groups (e.g. a croissant) are added to the cart
          as-is.
        </p>
      )}

      {groups.map((group, groupIdx) => (
        <fieldset
          key={group.groupId}
          className="rounded-2xl border border-wca-black/[0.08] bg-wca-chalk/30 p-4 space-y-3"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <input
              type="text"
              value={group.name}
              onChange={(e) => updateGroup(groupIdx, { name: e.target.value })}
              maxLength={40}
              className={cn(fieldClass, 'min-w-0 flex-1 py-2 font-medium')}
              placeholder="Group name"
            />
            <select
              value={group.type}
              onChange={(e) =>
                updateGroup(groupIdx, {
                  type: e.target.value as CustomizationGroupType,
                })
              }
              className={cn(fieldClass, 'w-auto py-1.5')}
            >
              <option value="single">Single (radio)</option>
              <option value="multi">Multi (checkbox)</option>
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={group.required}
                onChange={(e) => updateGroup(groupIdx, { required: e.target.checked })}
                className="w-4 h-4 accent-wca-red"
              />
              Required
            </label>
            <button
              type="button"
              onClick={() => removeGroup(groupIdx)}
              aria-label={`Remove ${group.name} group`}
              className="text-wca-gray-mid hover:text-wca-red p-1"
            >
              <Trash2 size={16} />
            </button>
          </div>

          <ul className="space-y-2">
            {group.options.map((option, optIdx) => (
              <li
                key={option.optionId}
                className="grid grid-cols-12 gap-2 items-center rounded-lg bg-wca-chalk/50 p-2"
              >
                <input
                  type="text"
                  value={option.label}
                  onChange={(e) => updateOption(groupIdx, optIdx, { label: e.target.value })}
                  maxLength={40}
                  placeholder="Option label"
                  className="col-span-5 rounded-lg border border-wca-gray-light bg-wca-surface p-2 text-sm transition-colors focus:border-wca-red focus:outline-none focus:ring-2 focus:ring-wca-red/25"
                />
                <div className="col-span-3 flex items-center gap-1">
                  <span className="text-xs font-mono text-wca-gray-mid">$</span>
                  <input
                    type="number"
                    value={(option.priceDeltaCents / 100).toFixed(2)}
                    onChange={(e) =>
                      updateOption(groupIdx, optIdx, {
                        priceDeltaCents: Math.round(Number.parseFloat(e.target.value || '0') * 100),
                      })
                    }
                    step={0.25}
                    className="w-full rounded-lg border border-wca-gray-light bg-wca-surface p-2 text-sm font-mono transition-colors focus:border-wca-red focus:outline-none focus:ring-2 focus:ring-wca-red/25"
                  />
                </div>
                <label className="col-span-3 flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox"
                    checked={option.isDefault}
                    onChange={(e) => setDefault(groupIdx, optIdx, e.target.checked)}
                    className="w-4 h-4 accent-wca-red"
                  />
                  Default
                </label>
                <button
                  type="button"
                  onClick={() => removeOption(groupIdx, optIdx)}
                  aria-label={`Remove ${option.label} option`}
                  disabled={group.options.length <= 1}
                  className="col-span-1 text-wca-gray-mid hover:text-wca-red p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => addOption(groupIdx)}
            className="inline-flex items-center gap-1 text-sm text-wca-red hover:underline"
          >
            <Plus size={14} />
            Add option
          </button>
        </fieldset>
      ))}

      <button
        type="button"
        onClick={addGroup}
        className="inline-flex items-center gap-1 text-sm font-medium text-wca-red hover:underline"
      >
        <Plus size={16} />
        Add customization group
      </button>
    </div>
  );
}
